import React, { useState, useEffect, useRef } from 'react';
import styles from './Home.module.css';

interface GameData {
  _id: string;
  textTitle: string;
  imagePlayer: string;
  musicUrl: string;
  urlButton: string;
  fileDownloadUrl?: string;
}

interface CapybaraGameProps {
  gameKey?: any;
  gameSettings?: any;
  gameData?: GameData;
}

const CapybaraGame: React.FC<CapybaraGameProps> = ({ gameKey, gameSettings, gameData }) => {
  const [isDancing, setIsDancing] = useState(true);
  const [isMusicOn, setIsMusicOn] = useState(true);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [timer, setTimer] = useState(40);
  const [capybaraStyle, setCapybaraStyle] = useState({});
  const [score, setScore] = useState(0);
  const [capybaraMoveIntervalId, setCapybaraMoveIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [isPsychedelicActive, setIsPsychedelicActive] = useState(false);
  const [isPrePsychedelicActive, setIsPrePsychedelicActive] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [flipDirection, setFlipDirection] = useState(1);
  const [showVideo, setShowVideo] = useState(false);
  const [videoTimeoutId, setVideoTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const clickSoundRef = useRef<HTMLAudioElement>(null);

  // Получаем данные игры из gameData, gameSettings или используем fallback
  const playerImage = gameData?.imagePlayer || gameSettings?.imagePlayer || './capybara.png';
  
  // Получаем музыку из gameData, gameSettings или используем fallback
  const backgroundMusic = gameData?.musicUrl || gameSettings?.musicUrl || './song.mp3';
  
  // Получаем заголовок игры из gameData, gameSettings или используем fallback
  const gameTitle = gameData?.textTitle || gameSettings?.textTitle || 'Capybara Game';
  
  // Получаем URL кнопки из gameData или gameSettings
  const buttonUrl = gameData?.urlButton || gameSettings?.urlButton;
  
  // Получаем URL файла для скачивания
  const fileDownloadUrl = gameData?.fileDownloadUrl || gameSettings?.fileDownloadUrl;

  const requestFullscreen = (element: HTMLElement) => {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      (element as any).mozRequestFullScreen();
    } else if ((element as any).webkitRequestFullscreen) {
      (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      (element as any).msRequestFullscreen();
    }
  };

  useEffect(() => {
    if (isGameStarted) {
      setIsPrePsychedelicActive(true);

      const timerId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      const psychedelicTimeoutId = setTimeout(() => {
        setIsPrePsychedelicActive(false);
        setIsPsychedelicActive(true);
      }, 5000);

      const shakeTimeoutId = setTimeout(() => {
        setIsShaking(true);
      }, 15000);

      const videoTimeoutId = setTimeout(() => {
        setShowVideo(true);
      }, (45 - 1) * 1000);

      setVideoTimeoutId(videoTimeoutId);

      return () => {
        clearInterval(timerId);
        clearTimeout(psychedelicTimeoutId);
        clearTimeout(shakeTimeoutId);
        clearTimeout(videoTimeoutId);
      };
    }
  }, [isGameStarted]);

  useEffect(() => {
    if (timer <= 0) {
      setIsGameStarted(false);
      setIsDancing(false);
      setIsPsychedelicActive(false);
      setIsPrePsychedelicActive(false);
      setIsShaking(false);
      setShowVideo(true);
      // НЕ останавливаем музыку при окончании игры - она должна продолжать играть
      console.log('Музыка продолжает играть при окончании игры Capybara');
      if (capybaraMoveIntervalId) {
        clearInterval(capybaraMoveIntervalId);
      }
      if (videoTimeoutId) {
        clearTimeout(videoTimeoutId);
      }
    }
  }, [timer, capybaraMoveIntervalId, videoTimeoutId]);

  const startGame = () => {
    setIsGameStarted(true);
    setTimer(30);
    setScore(0);
    moveCapybara();
    if (audioRef.current) {
      audioRef.current.play();
    }
    requestFullscreen(document.documentElement);
  };

  const toggleMusic = () => {
    setIsMusicOn((prev) => {
      const newStatus = !prev;
      if (audioRef.current && clickSoundRef.current) {
        audioRef.current.muted = !newStatus;
        clickSoundRef.current.muted = !newStatus;
      }
      return newStatus;
    });
  };

  const moveCapybara = () => {
    const randomX = Math.random() * 80;
    const randomY = Math.random() * 80;
    const randomSize = Math.random() * 50 + 50;
    const randomScale = Math.random() * 0.5 + 0.6;

    setFlipDirection((prev) => {
      const newFlipDirection = prev * -1;

      setCapybaraStyle({
        top: `${randomY}vh`,
        left: `${randomX}vw`,
        width: `${randomSize}px`,
        height: `${randomSize}px`,
        transform: `scale(${randomScale})`,
      });

      return newFlipDirection;
    });
  };

  const handleCapybaraClick = () => {
    if (isGameStarted) {
      setScore((prev) => prev + 1);
      moveCapybara();
      if (clickSoundRef.current) {
        clickSoundRef.current.play();
      }

      if (capybaraMoveIntervalId) {
        clearInterval(capybaraMoveIntervalId);
      }
      const newIntervalId = setInterval(() => {
        moveCapybara();
      }, 600);
      setCapybaraMoveIntervalId(newIntervalId);
    }
  };

  return (
    <div 
      className={`${styles.container} ${isPsychedelicActive ? styles.psychedelicActive : ''} ${isPrePsychedelicActive ? styles.prePsychedelicActive : ''} ${isShaking ? styles.shakingBackground : ''}`}
    >
      {!isGameStarted && !showVideo && (
        <button className={styles.startButton} onClick={startGame}>
          Start Game
        </button>
      )}
      {isGameStarted && !showVideo && (
        <>
          <button className={styles.musicButton} onClick={toggleMusic}>
            {isMusicOn ? 'Mute' : 'Unmute'}
          </button>
          <div
            className={`${styles.capybara} ${isDancing ? styles.dancing : ''}`}
            style={capybaraStyle}
            onClick={handleCapybaraClick}
          >
            <img
              src={playerImage}
              alt="Player"
              style={{ 
                width: '220%', 
                transform: `scaleX(${flipDirection})`
              }}
            />
          </div>
          <div className={styles.info}>
            <div className={styles.gameTitle}>{gameTitle}</div>
            Score: {score} | Remaining Time: {timer}s
          </div>
        </>
      )}
              {showVideo && (
          <div className={styles.videoContainer}>
            <div 
              style={{ 
                width: '100%', 
                height: '100%', 
                backgroundImage: 'url(./background.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{ textAlign: 'center', color: 'white', fontSize: '24px' }}>
                <h2>Игра завершена!</h2>
                <p>Ваш счет: {score}</p>
                <div className={styles.buttonContainer}>
                  {buttonUrl && (
                    <button 
                      className={styles.urlButton}
                      onClick={() => window.open(buttonUrl, '_blank')}
                    >
                      Перейти по ссылке
                    </button>
                  )}
                  {fileDownloadUrl && (
                    <button 
                      className={styles.urlButton}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = fileDownloadUrl;
                        link.download = '';
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      📥 Скачать файл
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      <audio ref={audioRef} src={backgroundMusic} loop />
      <audio ref={clickSoundRef} src="./click-sound.mp3" />
    </div>
  );
};

export default CapybaraGame;
